import _ from 'lodash'
import $ from 'cash-dom'
import {fmt, s, greyscale, palette, rewind, ffwd, cancel, offset, sfc32, quadMinMax, bezierMinMax} from './util.js'

export default class Matrix{
  constructor(book){
    this.book = book
    this.layout = Matrix.layout(this.book)
  }

  attach(dom){
    this.dom = $(dom).find('.diagram svg')
    this.layout = Matrix.layout(this.book)
    return this
  }

  static layout(book){
      let {illustrations, endings, choices, pages, stats} = book

      let PG_W = 5,
          PG_H = 5,
          PG_SPACE = 2;

      let width = (pages+1) * (PG_W+PG_SPACE),
          height = (pages+1) * (PG_H + PG_SPACE),
          depths = _.range(stats.depth+1).map(() => [])

      for (const src in choices){
        let pg = _.toNumber(src),
            depth = stats.pages.depths[pg] || 0,
            x = pg * (PG_W + PG_SPACE),
            y = height - pg * (PG_H + PG_SPACE),
            yMin = _.min([y, height - _.max(choices[pg]) * (PG_H + PG_SPACE)]),
            yMax = _.max([y, height - _.min(choices[pg]) * (PG_H + PG_SPACE)])

        depths[depth].push({
          pg,
          yMin, yMax,
          src: {x, y},
          opts: choices[pg].map(dst => {
            let y = height - dst * (PG_H + PG_SPACE) + (dst < pg ? .5 : -.5)
            return {x, y, dst, ending:endings.includes(dst), forced:choices[pg].length==1}
          })
        })
      }

      choices = _.fromPairs(depths.flat().map( ({pg, opts}) => [pg, opts]))
      return {width, height, depths, choices}
  }

  get svg(){
    let {width, height, depths} = this.layout,
        elts = []

    depths.forEach( (pages, depth) => {
      for (const page of pages){
        let {pg, yMin, yMax, opts} = page,
            {x, y} = page.src,
            [clr, dash, weight, offset] = _.size(opts)==1 ? ['#666', '1', '', 0]
                                                          : ['#aaa', '1,6', '7.2', 7]
        if (yMin < y) elts.push(
          `<line class="page depth-${depth}" x1="${x}" y1="${y-offset}" x2="${x}" y2="${yMin-1}" stroke="${clr}" stroke-dasharray="${dash}" stroke-width="${weight}"/>`
        )
        if (yMax > y) elts.push(
          `<line class="page depth-${depth}" x1="${x}" y1="${y+offset}" x2="${x}" y2="${yMax+1}" stroke="${clr}" stroke-dasharray="${dash}" stroke-width="${weight}"/>`
        )

        for (const {x, y, ending, forced} of opts){
          let dotsize = 3
          let [dotfill, dotstroke] = ending ? ['skyblue', 'transparent']
                                   : forced ? ['black', '#666']
                                   : ['#ccc', 'transparent']

          elts.push(`<circle class="page depth-${depth}" cx="${x}" cy="${y}" r="${dotsize}" fill="${dotfill}" stroke="${dotstroke}"/>`)
        }

      }
    })

    return `<svg class="matrix" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">
      <line x1="0" y1="${height}" x2="${height}" y2="0" stroke="#444" />
      <g>
        ${elts.join('')}
        <g class="tendrils" />
      </g>
    </svg>`
  }

  setDepth(depth){
    const {entries, fromEntries} = Object

    let {width, height, depths, choices} = this.layout,
        maxDepth = depths.length - 1,
        pct = depth % 1,
        inv = 1 - pct,
        tendrils = []

    if (pct==0 && depth<maxDepth){
      // draw cursors around current set of choices
      let [stroke, fill, size] = ['#cc4c02', 'transparent', 6]
      for (const page of depths[Math.ceil(depth)] || []){
        let {src} = page
        tendrils.push(`<circle stroke="#cc4c02" fill="transparent" cx="${fmt(src.x)}" cy="${fmt(src.y)}"  r="${1}" />`)
      }
    }else if (pct){
      if (pct > 0.5){
        for (const page of depths[Math.ceil(depth)] || []){
          // telescope the next set of vertical lines
          let mix = (pct - .5) / .5,
              {pg, yMin, yMax, opts} = page,
              {x, y} = page.src,
              top = y + mix * (yMin-1 - y),
              bot = y + mix * (yMax+1 - y),
              [clr, dash, weight, offset] = _.size(opts)==1 ? ['#666', '1', '', 0]
                                                            : ['#aaa', '1,6', '7.2', 7]
          if (yMin < y) tendrils.push(
            `<line x1="${x}" y1="${y-offset}" x2="${x}" y2="${top}" stroke="${clr}" stroke-dasharray="${dash}" stroke-width="${weight}"/>`
          )
          if (yMax > y) tendrils.push(
            `<line x1="${x}" y1="${y+offset}" x2="${x}" y2="${bot}" stroke="${clr}" stroke-dasharray="${dash}" stroke-width="${weight}"/>`
          )

          // draw dots for choices that have been reached by the line
          for (const opt of page.opts || []){
            let dotsize = 3,
                [dotfill, dotstroke] = opt.ending ? ['skyblue', 'transparent']
                                     : opt.forced ? ['black', '#666']
                                     : ['#ccc', 'transparent']
            if (opt.y > top - 2*dotsize && opt.y < bot - 2*dotsize){
              tendrils.push(`<circle fill="${dotfill}" stroke="${dotstroke}" cx="${fmt(opt.x)}"  cy="${fmt(opt.y)}"  r="${dotsize}" />`)
            }
          }
        }
      }

      for (const page of depths[Math.floor(depth)] || []){
        for (const opt of page.opts || []){
          let {src} = page,
              dst = {y:opt.y, x:src.x + src.y - opt.y}

          // fade out the current-page markers
          if (pct<0.5){
            let mix = pct / .5
            let opacity = 1-mix
            tendrils.push(`<circle stroke="#cc4c02" fill="transparent" opacity="${opacity}" cx="${fmt(src.x)}" cy="${fmt(src.y)}"  r="${1}" />`)
          }

          // animate orange rings from the choices to their destination pages
          if (pct<0.25){
            // quick fade in
            let mix = Math.min(1, pct * 10),
                opacity = mix,
                dotsize = 6,
                clr = opt.ending ? 'skyblue' : '#cc4c02'
            tendrils.push(`<circle stroke="${clr}" fill="transparent" opacity="${opacity}" cx="${fmt(opt.x)}" cy="${fmt(opt.y)}"  r="${dotsize}" />`)
          }else{
            // horizontal slide
            let mix = Math.min(1, (pct - .25) / .5),
                dotsize = (1-mix) * 5 + (opt.ending ? 0 : 1),
                clr = opt.ending ? 'skyblue' : '#cc4c02',
                to = {x: opt.x + mix * (dst.x-opt.x), y: opt.y + mix * (dst.y-opt.y)}
            tendrils.push(`<circle stroke="${clr}" fill="transparent" cx="${fmt(to.x)}" cy="${fmt(to.y)}"  r="${dotsize}" />`)
          }

        }
      }

    }
    this.dom.find('.tendrils').html(tendrils.join(' '))
  }

}

